import React, { useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Button,
  EmptyState,
  IconButton,
  IconMore,
  IconPlus,
  IconUsers,
  Menu,
  MenuItem,
  ListPage,
  Pagination,
  StatusLabel,
  Table,
  TableSkeletonRows,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../../ui/v2';
import { api, apiEndpoints } from '../../utils/apiService';
import type { TableSortState } from '../../utils/listOrdering';
import { CreateAgentDialog, EditAgentDialog } from './AgentDialogs';
import { TableSearch } from './TableSearch';
import {
  type AdminAssistant,
  type AgentFormData,
  type AgentSortKey,
  EMPTY_AGENT_FORM,
  type PickerOption,
  agentFormFrom,
  agentPayload,
  agentResponseError,
  agentStoreLabel,
  agentThrownError,
  toComboboxOptions,
  updateAgentForm,
  validateAgentForm,
} from './agentsModel';

const COLUMNS: {
  key: string;
  label: string;
  width?: number;
  sortKey?: AgentSortKey;
}[] = [
  { key: 'store', label: 'Store', width: 220, sortKey: 'store_name' },
  { key: 'assistant_id', label: 'Agent ID', sortKey: 'assistant_id' },
  {
    key: 'provider',
    label: 'Provider',
    width: 112,
    sortKey: 'assistant_provider',
  },
  {
    key: 'phone_number',
    label: 'Phone Number',
    width: 148,
    sortKey: 'phone_number',
  },
  { key: 'language', label: 'Language', width: 96, sortKey: 'language' },
  {
    key: 'llm_model',
    label: 'LLM Model',
    width: 148,
    sortKey: 'retell_llm_model',
  },
  { key: 'status', label: 'Status', width: 104, sortKey: 'is_active' },
  { key: 'actions', label: 'Actions', width: 80 },
];

const Placeholder: React.FC<{ value?: string | null }> = ({ value }) =>
  value && value !== '—' ? (
    <>{value}</>
  ) : (
    <span className="text-v2-fg-3">—</span>
  );

const AgentRowMenu: React.FC<{ label: string; onEdit: () => void }> = ({
  label,
  onEdit,
}) => {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  return (
    <span className="relative inline-block">
      <IconButton
        ref={anchor}
        size="sm"
        label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <IconMore />
      </IconButton>
      <Menu
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchor}
        align="right"
        width={160}
      >
        <MenuItem
          onClick={() => {
            setOpen(false);
            onEdit();
          }}
        >
          Edit Agent
        </MenuItem>
      </Menu>
    </span>
  );
};

const AgentsTab: React.FC<{
  assistants: AdminAssistant[];
  loading: boolean;
  page: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onChanged: () => void;
  storeOptions: PickerOption[];
  loadingStores: boolean;
  retellAgentOptions: PickerOption[];
  loadingRetellAgents: boolean;
  phoneNumberOptions: PickerOption[];
  loadingPhoneNumbers: boolean;
  onOpenCreate: () => void;
  manualAgentOption: string;
  retellLlmModels: readonly string[];
  search?: string;
  onSearchChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  sort?: TableSortState<AgentSortKey>;
  onSort?: (key: AgentSortKey) => void;
  filtering?: boolean;
}> = ({
  assistants,
  loading,
  page,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  onChanged,
  storeOptions,
  loadingStores,
  retellAgentOptions,
  loadingRetellAgents,
  phoneNumberOptions,
  loadingPhoneNumbers,
  onOpenCreate,
  search = '',
  onSearchChange,
  sort = { key: null, direction: 'asc' },
  onSort,
  filtering = false,
  manualAgentOption,
  retellLlmModels,
}) => {
  const { accessToken } = useAuth();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<AgentFormData>(EMPTY_AGENT_FORM);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedRetellAgentId, setSelectedRetellAgentId] =
    useState(manualAgentOption);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedAssistant, setSelectedAssistant] =
    useState<AdminAssistant | null>(null);
  const [editForm, setEditForm] = useState<AgentFormData>(EMPTY_AGENT_FORM);
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');
  const [updating, setUpdating] = useState(false);

  const retellAgentComboboxOptions = useMemo(
    () => toComboboxOptions(retellAgentOptions),
    [retellAgentOptions],
  );
  const phoneNumberComboboxOptions = useMemo(
    () => toComboboxOptions(phoneNumberOptions),
    [phoneNumberOptions],
  );

  const openCreate = () => {
    setIsCreateOpen(true);
    setCreateError('');
    setCreateSuccess('');
    onOpenCreate();
  };

  const closeCreate = () => {
    setIsCreateOpen(false);
    setCreateError('');
    setCreateSuccess('');
    setSelectedRetellAgentId(manualAgentOption);
  };

  const handleCreateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setCreateForm((prev) =>
      updateAgentForm(prev, { name, value, type, checked }),
    );
  };

  const handleRetellAgentSelect = (agentId: string) => {
    setSelectedRetellAgentId(agentId);
    setCreateForm((prev) => ({
      ...prev,
      assistant_id: agentId === manualAgentOption ? '' : agentId.trim(),
    }));
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError('');
    setCreateSuccess('');
    setSubmitting(true);

    if (!accessToken) {
      setCreateError('You must be logged in to create an agent');
      setSubmitting(false);
      return;
    }
    const invalid = validateAgentForm(createForm);
    if (invalid) {
      setCreateError(invalid);
      setSubmitting(false);
      return;
    }

    try {
      const response = await api.post(
        apiEndpoints.createAssistant(),
        agentPayload(createForm),
      );
      if (response && response.ok) {
        await response.json();
        setCreateSuccess('Agent created successfully!');
        setCreateForm(EMPTY_AGENT_FORM);
        setSelectedRetellAgentId(manualAgentOption);
        onChanged();
      } else {
        setCreateError(await agentResponseError(response, 'create'));
      }
    } catch (err) {
      console.error('Assistant creation error:', err);
      setCreateError(agentThrownError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (assistant: AdminAssistant) => {
    setSelectedAssistant(assistant);
    setEditForm(agentFormFrom(assistant));
    setEditError('');
    setEditSuccess('');
    setIsEditOpen(true);
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) =>
      updateAgentForm(prev, { name, value, type, checked }),
    );
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditError('');
    setEditSuccess('');
    setUpdating(true);

    if (!accessToken) {
      setEditError('You must be logged in to update an agent');
      setUpdating(false);
      return;
    }
    if (!selectedAssistant) {
      setEditError('No agent selected');
      setUpdating(false);
      return;
    }
    const invalid = validateAgentForm(editForm);
    if (invalid) {
      setEditError(invalid);
      setUpdating(false);
      return;
    }

    try {
      const updatePayload: ReturnType<typeof agentPayload> & { id?: number } =
        agentPayload(editForm);
      if (selectedAssistant.id) {
        updatePayload.id = selectedAssistant.id;
      }
      const response = await api.post(
        apiEndpoints.createAssistant(),
        updatePayload,
      );
      if (response && response.ok) {
        await response.json();
        setEditSuccess('Agent updated successfully!');
        onChanged();
        setTimeout(() => {
          setIsEditOpen(false);
          setSelectedAssistant(null);
          setEditSuccess('');
        }, 1500);
      } else {
        setEditError(await agentResponseError(response, 'update'));
      }
    } catch (err) {
      console.error('Assistant update error:', err);
      setEditError(agentThrownError(err));
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <ListPage
        title="Agents"
        toolbar={
          <div className="flex min-h-11 items-center px-4 py-2 lg:px-6">
            <TableSearch
              label="Search agents"
              placeholder="Search store, agent id, phone, or model…"
              value={search}
              onChange={onSearchChange ?? (() => undefined)}
            />
          </div>
        }
        actions={
          <Button
            variant="primary"
            leading={<IconPlus size={14} />}
            onClick={openCreate}
          >
            Create
          </Button>
        }
      >
        {!loading && assistants.length === 0 ? (
          <EmptyState
            icon={<IconUsers />}
            title={
              filtering
                ? 'No agents match that search'
                : "You don't have any agents at the moment"
            }
            description={
              filtering
                ? 'Try a store, agent id, phone number, or model.'
                : "When agents appear, they'll show up here."
            }
          />
        ) : (
          <>
            {loading && (
              <p role="status" className="sr-only">
                Loading agents…
              </p>
            )}
            <div className="v2-scroll overflow-x-auto">
              <Table fixed className="min-w-[1100px]">
                <THead>
                  <tr>
                    {COLUMNS.map((column) => (
                      <TH
                        key={column.key}
                        width={column.width}
                        sortable={Boolean(column.sortKey) && !loading}
                        active={
                          Boolean(column.sortKey) && sort.key === column.sortKey
                        }
                        dir={sort.direction}
                        onSort={() =>
                          column.sortKey && onSort?.(column.sortKey)
                        }
                      >
                        {column.label}
                      </TH>
                    ))}
                  </tr>
                </THead>
                <TBody>
                  {loading ? (
                    <TableSkeletonRows cols={COLUMNS.length} />
                  ) : (
                    assistants.map((assistant, idx) => (
                      <TR key={assistant.id || idx}>
                        <TD truncate className="font-medium">
                          <Placeholder value={agentStoreLabel(assistant)} />
                        </TD>
                        <TD truncate num>
                          <Placeholder value={assistant.assistant_id} />
                        </TD>
                        <TD truncate>
                          <Placeholder value={assistant.assistant_provider} />
                        </TD>
                        <TD truncate num>
                          <Placeholder value={assistant.phone_number} />
                        </TD>
                        <TD truncate>
                          <Placeholder value={assistant.language} />
                        </TD>
                        <TD truncate num>
                          <Placeholder value={assistant.retell_llm_model} />
                        </TD>
                        <TD>
                          <StatusLabel
                            tone={assistant.is_active ? 'success' : 'neutral'}
                          >
                            {assistant.is_active ? 'Active' : 'Inactive'}
                          </StatusLabel>
                        </TD>
                        <TD>
                          <AgentRowMenu
                            label={`Actions for ${
                              assistant.assistant_id || 'agent'
                            }`}
                            onEdit={() => openEdit(assistant)}
                          />
                        </TD>
                      </TR>
                    ))
                  )}
                </TBody>
              </Table>
            </div>
          </>
        )}

        {totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onPage={onPageChange}
            noun="agents"
          />
        )}
      </ListPage>

      <EditAgentDialog
        open={isEditOpen}
        form={editForm}
        error={editError}
        success={editSuccess}
        updating={updating}
        storeOptions={storeOptions}
        loadingStores={loadingStores}
        retellLlmModels={retellLlmModels}
        onChange={handleEditChange}
        onStoreChange={(value) =>
          setEditForm((prev) => updateAgentForm(prev, { name: 'store', value }))
        }
        onSubmit={handleEditSubmit}
        onClose={() => setIsEditOpen(false)}
      />

      <CreateAgentDialog
        open={isCreateOpen}
        form={createForm}
        error={createError}
        success={createSuccess}
        submitting={submitting}
        storeOptions={storeOptions}
        loadingStores={loadingStores}
        retellAgentOptions={retellAgentComboboxOptions}
        loadingRetellAgents={loadingRetellAgents}
        selectedRetellAgentId={selectedRetellAgentId}
        manualAgentOption={manualAgentOption}
        phoneNumberOptions={phoneNumberComboboxOptions}
        loadingPhoneNumbers={loadingPhoneNumbers}
        retellLlmModels={retellLlmModels}
        onChange={handleCreateChange}
        onStoreChange={(value) =>
          setCreateForm((prev) =>
            updateAgentForm(prev, { name: 'store', value }),
          )
        }
        onRetellAgentChange={handleRetellAgentSelect}
        onPhoneNumberChange={(value) =>
          setCreateForm((prev) =>
            updateAgentForm(prev, { name: 'phone_number', value }),
          )
        }
        onSubmit={handleCreateSubmit}
        onClose={closeCreate}
      />
    </>
  );
};

export default AgentsTab;
